// Section file — populated during component conversion.
import React from "react";

const lang = {
  balance: {
    title: "Add Balance",
    subtitle: "Top up your boost balance to send instant engagement.",
    errSelectBoth: "Please select both an amount and a payment method",
    errFillCardDetails: "Please fill in all card details",
    errPaymentFailed: "Payment failed",
    errPaymentFailedTryAgain: "Payment failed. Please try again.",
    errSelectAmount: "Please select an amount",
    selectAmount: "Select Amount",
    // Bonus balance shown on each top-up tile.
    getsBalance: (credit) => `Get $${credit}`,
    bonusAmount: (bonus) => `+$${bonus} bonus`,
    customNoBonus: "No bonus",
    youReceive: "You receive",
    custom: "Custom",
    enterAmountPlaceholder: "Enter amount",
    minTopup: (min) => `Minimum top-up is $${min}.`,
    total: "Total",
    paymentMethod: "Payment Method",
    addNewCard: "Add New Card",
    cardNumber: "Card Number",
    expiry: "Expiry",
    cvv: "CVV",
    processing: "Processing...",
    pay: (amount) => `Pay $${amount}`,
    secure: "Secure",
    instant: "Instant",
    encrypted: "Encrypted",
  },

  tools: {
    items: {
      trends: { label: "Trends Analyzer", desc: "AI-powered trends analysis" },
      story: { label: "Story Viewer", desc: "AI-powered story viewer" },
      download: { label: "Post Downloader", desc: "AI-powered post downloader" },
      caption: { label: "Caption Generator", desc: "AI-powered caption generation" },
      idea: { label: "Idea Generator", desc: "AI-powered idea generation" },
      profile: { label: "Profile Enhancer", desc: "AI-powered profile enhancement" },
      photo: { label: "Photo Reviewer", desc: "AI-powered photo review" },
      collab: { label: "Collab Finder", desc: "AI-powered collaboration finder" },
    },
    basicFeature: "Basic Feature",
    proFeature: "Pro Feature",
    tierBasicOrPro: "Basic or Pro",
    tierPro: "Pro",
    upgradeToUnlock: (tier, desc) => `Upgrade to ${tier} to unlock ${desc}`,
    upgradeNow: "Upgrade Now",
    badgePro: "PRO",
    badgeBasic: "BASIC",
    startingDelivery: (type, amount, link) =>
      `Starting delivery for ${type}!\nAmount: ${amount}\nPost Link: ${link}`,
  },

  toolsCaption: {
    heading: "Caption Generator",
    subtitle: "Upload a photo and generate a caption that's guaranteed to go viral.",
    copied: "Copied to clipboard!",
    uploadError: (msg) => `An error occurred while uploading the image: ${msg}`,
    generateError: (msg) => `An error occurred while generating the caption: ${msg}`,
    unknownError: "Unknown error",
    success: "Captions generated successfully.",
    closeAria: "Close",
    uploadTitle: "Click to upload an image",
    generateButton: "Generate Caption",
  },

  toolsCollab: {
    heading: "Collab Finder",
    subtitle: "Enter an Instagram username to find likeminded profiles to connect with.",
    enterUsername: "Please enter an Instagram username",
    success: (count) => `Successfully found ${count} profiles.`,
    fetchFailed: "Failed to fetch competitors",
    usernamePlaceholder: "Username",
    analyzing: (count) => `Analyzing... (${count} profiles scanned)`,
    analyze: "Analyze",
    similarityTooltip: "AI-calculated similarity score based on content, audience, and engagement patterns",
  },

  toolsDownload: {
    heading: " Post Downloader ",
    subtitle: "Enter a username or link to download.",
    success: "Successfully found posts to download.",
    downloadError: (msg) => `An error occurred while downloading the post: ${msg}`,
    inputLabel: "Username or Post URL",
    inputPlaceholder: "Enter a username or post link",
    buttonEmpty: "Enter a username or post link",
    buttonDownloadPost: "Download Post",
    buttonSearchProfile: "Search Profile",
    noPosts: " No posts were found",
    tabPosts: "Posts",
    tabReels: "Reels",
    videoUnsupported: "Your browser does not support the video tag.",
    loading: "Loading...",
    loadMore: "Load More",
    noMoreData: "No More Data",
  },

  toolsIdea: {
    heading: "Idea Generator",
    subtitle: "Create unique video ideas using the power of AI.",
    success: "Ideas generated successfully.",
    generateError: (msg) => `An error occurred while generating the idea: ${msg}`,
    unknownError: "Unknown error",
    goalLabel: "Select Your Goal",
    goalAria: "Select your goal",
    goals: {
      none: "-- Choose a Goal --",
      brandAwareness: "Increase Brand Awareness",
      boostEngagement: "Boost Engagement",
      productLaunch: "Launch a New Product",
      increaseSales: "Increase Sales",
      educateAudience: "Educate Audience",
    },
    targetAudienceLabel: "Target Audience",
    targetAudiencePlaceholder: "Who are you targeting? e.g. Young adults, tech enthusiasts",
    topicLabel: "Topic / Niche",
    topicPlaceholder: "e.g. Fashion, Technology, Cooking",
    toneLabel: "Preferred Tone",
    toneAria: "Select your tone",
    tones: {
      none: "-- Choose a Tone --",
      informative: "Informative",
      casual: "Casual",
      humorous: "Humorous",
      persuasive: "Persuasive",
    },
    generateButton: "Generate Idea",
  },

  toolsPhoto: {
    heading: "Photo Reviewer",
    subtitle: "Upload a photo to get AI-powered suggestions for maximizing engagement and improving your content.",
    uploadError: (msg) => `An error occurred while uploading the image: ${msg}`,
    unknownError: "Unknown error",
    selectImage: "Please select an image to review",
    invalidResponse: "Invalid response format from server",
    unexpectedError: "An unexpected error occurred",
    reviewError: (msg) => `An error occurred while reviewing the photo: ${msg}`,
    success: "Review generated successfully.",
    closeAria: "Close",
    uploadTitle: "Click to upload an image",
    reviewing: "Reviewing...",
    reviewButton: "Review Photo",
    sectionVisualAppeal: "Visual Appeal",
    sectionComposition: "Composition",
    sectionEngagementPotential: "Engagement Potential",
    sectionOptimizationTips: "Optimization Tips",
  },

  toolsProfile: {
    heading: "Profile Enhancer",
    subtitle: "Enter your username to get an AI-powered profile makeover with a before & after comparison.",
    success: "Profile analyzed successfully.",
    scanError: (msg) => `An error occurred while scanning profile: ${msg}`,
    unknownError: "Unknown error",
    usernamePlaceholder: "Enter your username",
    analyzing: "Analyzing...",
    scanButton: "Scan Profile",
    posts: "Posts",
    followers: "Followers",
    following: "Following",
    noBio: "No bio",
    before: "BEFORE",
    after: "AFTER",
    whatWeChanged: "What we changed & why",
    bio: "Bio",
    displayName: "Display Name",
    profilePhoto: "Profile Photo",
    quickWins: "Quick wins to implement now",
  },

  toolsStory: {
    heading: "Story Viewer",
    subtitle: "Anonymously view someones live story.",
    success: "Story viewed successfully.",
    viewError: (msg) => `An error occurred while viewing the story: ${msg}`,
    unknownError: "Unknown error",
    usernameLabel: "Username",
    usernamePlaceholder: "Enter a username",
    viewButton: "View Story",
    noPosts: "No posts were found",
    videoUnsupported: "Your browser does not support the video tag.",
  },

  toolsTrends: {
    heading: "Trends Analyzer",
    subtitle: "Get real-time insights about current Instagram/TikTok trends and viral content strategies relevant to you.",
    invalidData: "Invalid trends data structure",
    invalidResponse: "Invalid response format from server",
    unexpectedError: "An unexpected error occurred",
    success: "Trends analyzed successfully.",
    analyzeError: (msg) => `An error occurred while analyzing trends: ${msg}`,
    ranges: {
      week: "Past Week",
      month: "Past Month",
      year: "Past Year",
    },
    customFocusLabel: "Custom Focus (Optional)",
    customFocusPlaceholder: "E.g. Dance trends, beauty products, Drake, etc.",
    analyzing: (count) => `Analyzing... (${count} posts processed)`,
    analyzeButton: "Analyze Current Trends",
    sections: {
      trends: "Trending Content",
      hooks: "Video Hooks",
      audio: "Popular Audio",
      engagement: "Engagement Tactics",
      themes: "Content Themes",
      technical: "Technical Strategies",
    },
  },

  analytics: {
    welcomeBack: "Welcome back,",
    basicPlan: "Basic Plan",
    balanceLabel: "Balance:",
    loading: "Loading",
    followers: "Followers",
    following: "Following",
    tabOverview: "Overview",
    tabGrowthSettings: "Growth Settings",
    linkHeading: "Link your Instagram profile",
    linkSubtitle: "Your account is 1 step away from growing!",
    instagramPlaceholder: "Enter your Instagram username",
    instagramAria: "Instagram Username",
    linkAccount: "Link Account",
    profileLinked: (username) => `Profile Linked! Welcome back ${username}`,
    settingsSaved: (interests, ageRange, gender, instagram) =>
      `Settings Saved!\nInterests: ${interests}\nAge Range: ${ageRange}\nGender: ${gender}\nInstagram: ${instagram}`,
    saveFailed: (msg) => `Failed to save settings: ${msg}`,
    enterValidUsername: "Please enter a valid Instagram username.",
    linkFailed: (msg) => `Failed to link Instagram account: ${msg}`,
    growthSettingsHeading: "Growth Settings",
    growthSettingsSubtitle: "Adjust your growth preferences here.",
    selectInterests: "Select Target Interests",
    interests: {
      fitness: "Fitness",
      travel: "Travel",
      fashion: "Fashion",
      tech: "Technology",
      food: "Food",
      music: "Music",
      art: "Art",
      gaming: "Gaming",
      photography: "Photography",
      sports: "Sports",
      health: "Health",
      beauty: "Beauty",
      books: "Books",
      movies: "Movies",
      pets: "Pets",
    },
    targetAgeRange: "Target Age Range",
    ageRangeYears: (low, high) => `${low} - ${high} years`,
    targetGender: "Target Gender",
    male: "Male",
    female: "Female",
    both: "Both",
    saveSettings: "Save Settings",
  },

  checkout: {
    title: "Checkout",
    paymentError: (msg) => `Error processing payment: ${msg}`,
    unknownError: "Unknown error",
    purchaseSuccessful: "Purchase Successful!",
    close: "Close",
    planTitle: (planName) => `${planName} Plan`,
    annualSubscription: "Annual Subscription",
    monthlySubscription: "Monthly Subscription",
    discountApplied: "50% OFF Applied!",
    subtotal: "Subtotal",
    discount: "Discount",
    total: "Total",
    paymentMethod: "Payment Method",
    addNewCard: "Add New Card",
    cardNumberPlaceholder: "Card Number",
    cvvPlaceholder: "CVV",
    processing: "Processing...",
    purchase: "Purchase",
  },

  buySpins: {
    title: "Buy Spins",
    subtitle: "Extra spins are added to your account instantly.",
    packs: {
      single: { label: "1 Extra Spin" },
      pack: { label: "5 Spins Pack", badge: "Save $5" },
    },
    errSelectPayment: "Please select a payment method.",
    errFillCardDetails: "Fill in all card details.",
    errPurchaseFailed: "Purchase failed.",
    selectPack: "Select Pack",
    spinsCount: (n) => `${n} spin${n === 1 ? "" : "s"}`,
    paymentMethod: "Payment Method",
    addNewCard: "Add New Card",
    cardNumber: "Card Number",
    expiry: "Expiry",
    cvv: "CVV",
    processing: "Processing…",
    pay: (price) => `Pay $${price}`,
    secure: "Secure",
    instant: "Instant",
    encrypted: "Encrypted",
  },

  followerChart: {
    followers: "Followers",
  },

  profileSwitcher: {
    addAccount: "Add Account",
    badgePro: "PRO",
    badgeBasic: "BASIC",
    badgeStarter: "STARTER",
    badgeNoPlan: "NO PLAN",
    addAnotherAccount: "Add Another Account",
  },

  visualizer: {
    plans: {
      starter: {
        name: "Starter",
        tagline: "A simple way to start growing.",
        features: [
          "Followers",
          "Follower growth starts instantly",
          "AI audience targeting",
        ],
      },
      basic: {
        name: "Core",
        tagline: "Stronger, more consistent growth.",
        features: [
          "Followers",
          "Follower growth starts instantly",
          "Enhanced AI audience targeting",
          "AI Content Assistant",
          "Priority support",
        ],
      },
      pro: {
        name: "Elite",
        tagline: "Highest quality, highest-priority growth.",
        features: [
          "Followers",
          "Follower growth starts instantly",
          "Premium AI audience targeting",
          "Dedicated Account Manager",
          "Priority support & delivery",
        ],
      },
    },
    scanSteps: [
      "Finding your profile",
      "Analyzing your audience",
      "Identifying your niche",
      "Calculating growth potential",
    ],
    errEnterUsername: "Please enter a username and select a platform",
    errFetchFailed: "Failed to fetch profile data",
    badge: "Start Growing In Seconds",
    headingJSX: (
      <>
        See your <span className="text-instagram">exact growth</span> potential
      </>
    ),
    subheading: "Enter your username to start growing — we'll show you exactly how many followers you'll gain.",
    recommendedForYou: "Recommended for you",
    choosePlatform: "Choose Platform",
    // Free-boost claim flow — replaces the scan as the way into the plans.
    boostBadge: "Free Followers — No Password",
    boostHeadingJSX: (
      <>
        Claim your <span className="text-instagram">free followers</span>
      </>
    ),
    boostSubheading: "Enter your username and we'll send your free follower boost right now — no password, no card.",
    // Wordmark under the gift, and the word that detonates across the screen.
    boostBrandLine1: "Free Follower",
    boostBrandLine2: "Boost",
    boostWord: "BOOST!",
    boostCta: "Claim Free →",
    boostFineprint: "One free boost per person.",
    boostActivating: "Activating your free followers...",
    boostSteps: [
      "Locating your account",
      "Reserving your free followers",
      "Sending them to your profile",
      "Building your growth plan",
    ],
    boostDelivered: (n) => `${n} free followers are on the way`,
    boostAlreadyClaimed: "You've already claimed your free boost — here's your growth plan.",
    boostPrivateWarning: "Your account is private. Set it to public so we can deliver your followers.",
    customizeTitle: "Customize Your Followers",
    // Prefixed to the subtitle with the selected plan's monthly follower count.
    customizeFollowerCount: (n) => `${n} Followers`,
    customizeSubtitle: "Fine-tune who we target and how fast you grow.",
    // Paid customization add-ons.
    addonIncluded: "Included",
    addonPlus: (p) => `+$${p}`,
    addonTotalLabel: "New total",
    targetingLabel: "Targeting",
    femaleOnlyTitle: "Female followers only",
    femaleOnlySub: "We'll target women exclusively.",
    usaOnlyTitle: "USA followers only",
    usaOnlySub: "We'll target United States accounts exclusively.",
    customizeContinue: "Continue to Checkout →",
    customizeBack: "← Back to plans",
    deliverySpeedLabel: "Delivery Speed",
    speedTurbo: "Turbo",
    speedFast: "Super Instant",
    speedGradual: "Gradual",
    // Delivery window shown under each speed name.
    speedTurboSub: "Instant",
    speedFastSub: "Delivery starts in seconds.",
    speedGradualSub: "Delivered over time",
    audienceLabel: "Audience",
    audienceEveryone: "Everyone",
    audienceMen: "Men",
    audienceWomen: "Women",
    locationLabel: "Location",
    locationUsa: "USA",
    locationWorldwide: "Worldwide",
    locationChoose: "Choose Country",
    countryPlaceholder: "Select a country",
    handlePlaceholder: "yourhandle",
    search: "Search",
    analyze: "Analyze →",
    noPassword: "No password",
    takesSeconds: "Takes seconds",
    analyzingAccount: "Setting up your growth...",
    currentlyFollowers: (n) => (
      <>
        Currently <strong style={{ color: "#0a2540" }}>{n}</strong> followers
      </>
    ),
    projectedGrowth: "Projected Growth",
    youllGain: (n) => (
      <>
        You'll gain <strong style={{ color: "#0a2540" }}>{n}</strong> new followers
      </>
    ),
    growthPct: (pct) => `+${pct}% growth`,
    before: "Before",
    currentFollowers: "Current followers",
    after30Days: "After",
    projectedFollowers: "Expected followers",
    perMo: "/mo",
    fiftyOff: "50% OFF",
    whatsIncluded: "What's included",
    followersPerMonth: (low, high) => `${high}+ Followers`,
    choosePlan: "Start Growing →",
    noPasswordRequired: "No password required",
    cancelAnytime: "Cancel anytime",
    tryDifferent: "← Try a different username",
  },
};

export default lang;
